import {AlertModal} from 'presentation/components';
import {HealthDuplicityDialog} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthSpecialtySelection/components/HealthDuplicityDialog';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {PendingDialog} from 'presentation/modules/booking/shared/ui/components/PendingDialog';
import {ScriptModal} from 'presentation/modules/booking/shared/ui/components/ScriptModal';
import {TherapyAlert} from 'presentation/modules/booking/shared/ui/components/TherapyAlert';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {BookingVerificationsProps} from './types';
import {useNavigate} from 'react-router-dom';
import {HealthBookingEnum, TelehealthBookingEnum} from 'presentation/constants';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {SimpleConfirmationDrawer} from 'presentation/pages/private/booking/components/ConfirmationDrawer/SimpleConfirmationDrawer';
import {useBookingVerifications} from './useBookingVerifications';

const {serverError, serverErrorTitle, tryAgain, unexpectedError} = translations['pt-br'].commons;

const BookingVerifications = ({
	checkBookingModal,
	setCheckBookingModal,
	loadingText,
	resetBookingDataSpecialties,
	handleChooseScriptSuggestion,
	scheduledBooking,
	swapSpecialtyCallback,
	selectedProfessional,
	handleResetProfessionalSelection,
}: BookingVerificationsProps) => {
	const navigate = useNavigate();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);

	const {
		isPending,
		showDuplicityDialog,
		isScriptState,
		showTherapyAlert,
		unexpectedErrorState,
		openConfirmationDrawer,
		setOpenConfirmationDrawer,
		duplicityModalWasOpened,
		isConfirmingPending,
		setIsConfirmingPending,
		handleClose,
	} = useBookingVerifications({
		checkBookingModal,
		setCheckBookingModal,
		resetBookingDataSpecialties,
		selectedProfessional,
		scheduledBooking,
	});

	if (isPending) {
		return <PendingDialog open text={loadingText} />;
	}

	if (showDuplicityDialog && scheduledBooking) {
		return (
			<HealthDuplicityDialog
				open
				setOpen={handleClose}
				scheduledBooking={scheduledBooking}
				swapSpecialtyCallback={async () => {
					handleClose();
					setOpenConfirmationDrawer(true);
				}}
			/>
		);
	}

	if (isScriptState) {
		return <ScriptModal open setOpen={handleClose} handleNext={handleChooseScriptSuggestion} />;
	}

	if (showTherapyAlert) {
		return (
			<TherapyAlert
				flowType={FlowEnum.HEALTH}
				alertType="needAuth"
				open
				setOpen={async () => {
					handleClose();
					setOpenConfirmationDrawer(false);
				}}
			/>
		);
	}

	if (unexpectedErrorState) {
		return (
			<AlertModal
				open
				onClose={handleClose}
				description={serverError}
				title={serverErrorTitle}
				headerTitle={unexpectedError}
				buttonLabel={tryAgain}
				variant="info"
			/>
		);
	}

	return (
		<Fragment>
			<PendingDialog open={isConfirmingPending} text={loadingText} />
			<SimpleConfirmationDrawer
				handleOpen={() => setOpenConfirmationDrawer(true)}
				handleClose={() => {
					setOpenConfirmationDrawer(false);
					if (handleResetProfessionalSelection) handleResetProfessionalSelection();
				}}
				isOpened={openConfirmationDrawer}
				handleConfirmAndContinue={async () => {
					setIsConfirmingPending(true);
					try {
						if (duplicityModalWasOpened) {
							const bookingTypeIsTelemedicine =
								bookingData.bookingType === BookingTypeEnum.TELEHEALTH;
							const scheduledBookingIsTelemedicine =
								scheduledBooking?.bookingType === BookingTypeEnum.TELEHEALTH;
							const userChoseInPerson =
								bookingData.bookingType === BookingTypeEnum.HEALTH;
							if (
								bookingTypeIsTelemedicine ||
								(scheduledBookingIsTelemedicine && !userChoseInPerson)
							) {
								setBookingData(prev => ({
									...prev,
									specialtyCode: scheduledBooking?.specialtyCode ?? '',
									subSpecialtyCode:
										!scheduledBooking?.subSpecialtyCode ||
										scheduledBooking?.subSpecialtyCode === '0'
											? ''
											: scheduledBooking?.subSpecialtyCode,
								}));
								navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
								setOpenConfirmationDrawer(false);
								return;
							}
							if (swapSpecialtyCallback) {
								await swapSpecialtyCallback();
								setOpenConfirmationDrawer(false);
								return;
							}
						}
						navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW);
					} finally {
						setIsConfirmingPending(false);
					}
				}}
			/>
		</Fragment>
	);
};

export default BookingVerifications;
